import {useCallback, useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {PageTitle, StatBlock} from '../components/shared/badges';
import {asieApi} from '../lib/prospectionAsieApi';

const b = 'border border-slate-300 bg-white px-3 py-2 text-sm font-bold rounded hover:bg-slate-50 disabled:opacity-40';
const labels = {
  conformite_a_verifier: 'Règle pays à vérifier (Bloqué)',
  email_a_completer: 'Email acheteur à compléter',
  a_verifier: 'Preuve à vérifier',
  a_valider: 'Prêt à envoyer (À valider)',
  envoi_en_cours: 'Envoi en cours',
  envoi_incertain: 'Résultat SMTP incertain',
  accepte_smtp: 'Envoyé (Accepté SMTP)',
  oppose: 'Opposition / Liste noire',
  rejete: 'Refusé',
};
const badgeColors = {
  conformite_a_verifier: 'bg-purple-50 text-purple-900 border-purple-300',
  email_a_completer: 'bg-orange-50 text-orange-900 border-orange-300',
  a_verifier: 'bg-amber-50 text-amber-900 border-amber-300',
  a_valider: 'bg-emerald-50 text-emerald-900 border-emerald-300',
  envoi_en_cours: 'bg-sky-50 text-sky-900 border-sky-300',
  envoi_incertain: 'bg-red-50 text-red-900 border-red-300',
  accepte_smtp: 'bg-green-100 text-green-900 border-green-400',
  oppose: 'bg-slate-200 text-slate-800 border-slate-400',
  rejete: 'bg-rose-50 text-rose-900 border-rose-300',
};

const CONFORMITE_BADGES = {
  OPT_IN_STRICT: {label: 'Opt-in strict requis', cls: 'bg-amber-100 text-amber-900 border-amber-300'},
  B2B_LEGITIMATE_OPT_OUT: {label: 'Intérêt légitime B2B (Opt-out)', cls: 'bg-emerald-50 text-emerald-900 border-emerald-300'},
  A_VERIFIER: {label: 'Conformité pays à vérifier (Bloqué)', cls: 'bg-purple-100 text-purple-900 border-purple-300'},
  LOCAL_TN_B2B: {label: 'Marché Local TN · Seuil TND', cls: 'bg-sky-100 text-sky-900 border-sky-300'},
};

const DEFAULT_VILLES_TN = ['Tunis', 'Sfax', 'Sousse', 'Hammamet', 'Djerba'];
const DEFAULT_SEGMENTS_TN = {
  epicerie_fine: 'Épicerie fine & cave gourmande',
  supermarche_premium: 'Supermarché premium & rayon terroir',
  hotel_haut_de_gamme: 'Hôtel 4★/5★ & Resort (Achats F&B)',
  restaurant_gastronomique: 'Restaurant gastronomique & table premium',
  traiteur: 'Traiteur événementiel & réceptions',
  boutique_terroir: 'Boutique de produits du terroir & cadeaux',
};

const emptyForm = {
  pays: 'SG',
  societe: '',
  email: '',
  telephone: '',
  ville: 'Sfax',
  segment_local: 'epicerie_fine',
  langue_approche: '',
  contact: '',
  marque: '',
  source: 'salon',
  url_preuve: 'https://',
  reference_preuve: '',
  date_preuve: new Date().toISOString().slice(0, 10),
  hs: '1509.20',
  volume: 'palette_ou_plus',
  canal: 'premium',
  solvabilite: 'etablie_et_importateur',
  interet: 'confirme',
  participation_salon: true,
  autorisation_email: false,
  url_autorisation: '',
};

export default function ProspectionAsie() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [state, setState] = useState(null);
  const [complianceState, setComplianceState] = useState({catalogue: [], pays_bloques_avec_prospects: []});
  const [one, setOne] = useState(null);
  const [note, setNote] = useState('');
  const [refusalReason, setRefusalReason] = useState('');
  const [suppressEmail, setSuppressEmail] = useState('');
  const [suppressDomain, setSuppressDomain] = useState('');
  const [suppressPhone, setSuppressPhone] = useState('');
  const [suppressReason, setSuppressReason] = useState('');
  const [reconcileNote, setReconcileNote] = useState('');
  const [completeEmailValue, setCompleteEmailValue] = useState('');
  const [completeContactValue, setCompleteContactValue] = useState('');
  const [completeNoteValue, setCompleteNoteValue] = useState('');
  const [editObjet, setEditObjet] = useState('');
  const [editTexte, setEditTexte] = useState('');
  const [editTraduction, setEditTraduction] = useState('');
  const [editLangue, setEditLangue] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLead, setNewLead] = useState(emptyForm);
  const [selectedVillesTn, setSelectedVillesTn] = useState(DEFAULT_VILLES_TN);
  const [selectedSegmentTn, setSelectedSegmentTn] = useState('ALL');
  const [certifyForms, setCertifyForms] = useState({});
  const [filterScope, setFilterScope] = useState('ALL');
  const [filterCountry, setFilterCountry] = useState('ALL');
  const [filterPriority, setFilterPriority] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [csv, setCsv] = useState(null);

  const load = useCallback(async () => {
    const [l, s, c] = await Promise.all([
      asieApi(`/prospects?page=${page}`),
      asieApi('/etat'),
      asieApi('/pays-conformite').catch(() => ({catalogue: [], pays_bloques_avec_prospects: []})),
    ]);
    setList(l.items || []);
    setTotal(l.total || 0);
    setState(s);
    setComplianceState(c);
  }, [page]);

  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, [load]);

  async function action(fn) {
    setBusy(true);
    setError('');
    setInfo('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function syncDraftFields(row) {
    setEditObjet(row?.mail?.objet || '');
    setEditTexte(row?.mail?.texte || '');
    setEditTraduction(row?.mail?.traduction_fr || '');
    setEditLangue(row?.mail?.langue || row?.donnees?.langue_approche || '');
  }

  async function open(id) {
    const row = await asieApi(`/prospects/${id}`);
    setOne(row);
    setSuppressEmail(row.donnees.email || '');
    setSuppressDomain(row.donnees.email && row.donnees.email.includes('@') ? row.donnees.email.split('@')[1] : '');
    setSuppressPhone(row.donnees.telephone || '');
    setCompleteEmailValue(row.donnees.email || '');
    setCompleteContactValue(row.donnees.contact || '');
    setCompleteNoteValue('');
    setRefusalReason('');
    setSuppressReason('');
    setReconcileNote('');
    setNote('');
    syncDraftFields(row);
  }

  async function triggerExportSync() {
    await action(async () => {
      const res = await asieApi('/sync-export', 'POST', {hs_code: '1509.20', days: 7});
      setInfo(
        `Recherche douanière HS 1509.20 terminée (${res.mode_simulation ? 'Mode simulation hors-ligne' : 'API Volza + Apollo en direct'}) : ` +
          `${res.ajoutes} nouveau(x) prospect(s), ${res.existants} déjà connu(s), ${res.priorite_a_notifies} alerte(s) PWA Priorité A envoyée(s)` +
          (res.conformite_a_verifier ? `, ${res.conformite_a_verifier} bloqué(s) en attente de règle pays (${res.pays_a_verifier_detectes.join(', ')})` : '') +
          (res.email_a_completer ? `, ${res.email_a_completer} sans email B2B (à compléter).` : '.')
      );
    });
  }

  async function triggerLocalTnSync() {
    await action(async () => {
      const segs = selectedSegmentTn === 'ALL' ? [] : [selectedSegmentTn];
      const res = await asieApi('/sync-local-tn', 'POST', {villes: selectedVillesTn, segments: segs});
      setInfo(
        `Découverte Marché Local Tunisien terminée (${res.mode_simulation ? 'Mode simulation hors-ligne' : 'Google Places API en direct'}) : ` +
          `${res.ajoutes} établissement(s) ajouté(s), ${res.existants_whatsapp_phase1} déjà présent(s) dans WhatsApp Phase 1, ` +
          `${res.priorite_a_notifies} alerte(s) PWA Priorité A envoyée(s).`
      );
    });
  }

  async function importCsv() {
    if (!csv) return;
    const text = await csv.text();
    const res = await asieApi('/importer-csv', 'POST', text, true);
    setInfo(`${res.ajoutes} ajouté(s), ${res.existants} existant(s), ${res.opposes} en opposition.`);
    setCsv(null);
  }

  async function submitNewLead(e) {
    e.preventDefault();
    await action(async () => {
      const payload = {...newLead};
      if (payload.pays === 'LOCAL_TN') {
        payload.hs = '';
        if (payload.source === 'douane') payload.source = 'terrain_local';
      } else {
        payload.ville = '';
        payload.segment_local = '';
        payload.telephone = '';
        payload.langue_approche = '';
      }
      const created = await asieApi('/prospects', 'POST', payload);
      setInfo(`Prospect « ${created.donnees.societe} » créé avec un score de ${created.score.total}/100 (Priorité ${created.score.priorite}).`);
      setShowAddForm(false);
      setNewLead(emptyForm);
      setOne(created);
      syncDraftFields(created);
    });
  }

  async function certifyCountry(code) {
    const form = certifyForms[code] || {
      categorie_conformite: 'OPT_IN_STRICT',
      loi: 'Vérification juridique interne direction',
      exigence: 'Opt-in préalable ou intérêt légitime B2B vérifié.',
      langue_defaut: 'en_formel',
    };
    await action(async () => {
      const updated = await asieApi('/pays-conformite', 'POST', {code, ...form});
      setInfo(`Règle de conformité pour ${updated.nom} (${code}) enregistrée (${updated.categorie_conformite}). Les fiches en attente ont été débloquées.`);
      if (one && one.donnees.pays === code) {
        await open(one._id);
      }
    });
  }

  function toggleVilleTn(ville) {
    setSelectedVillesTn((prev) => (prev.includes(ville) ? prev.filter((v) => v !== ville) : [...prev, ville]));
  }

  const countriesOptions = useMemo(() => {
    if (complianceState.catalogue && complianceState.catalogue.length) {
      return complianceState.catalogue;
    }
    return [
      {code: 'LOCAL_TN', nom: 'Tunisie — Marché Local', categorie_conformite: 'LOCAL_TN_B2B'},
      {code: 'SG', nom: 'Singapour', categorie_conformite: 'B2B_LEGITIMATE_OPT_OUT'},
      {code: 'FR', nom: 'France', categorie_conformite: 'B2B_LEGITIMATE_OPT_OUT'},
      {code: 'US', nom: 'États-Unis', categorie_conformite: 'B2B_LEGITIMATE_OPT_OUT'},
      {code: 'JP', nom: 'Japon', categorie_conformite: 'OPT_IN_STRICT'},
      {code: 'DE', nom: 'Allemagne', categorie_conformite: 'OPT_IN_STRICT'},
      {code: 'CA', nom: 'Canada', categorie_conformite: 'OPT_IN_STRICT'},
      {code: 'AE', nom: 'Émirats Arabes Unis', categorie_conformite: 'A_VERIFIER'},
    ];
  }, [complianceState.catalogue]);

  const filteredList = useMemo(() => {
    return list.filter((row) => {
      const isLocal = row.donnees.pays === 'LOCAL_TN' || row.marche === 'LOCAL_TN';
      if (filterScope === 'EXPORT' && isLocal) return false;
      if (filterScope === 'LOCAL_TN' && !isLocal) return false;
      if (filterScope === 'WA_PHASE1' && !row.synergie_wa?.existant_whatsapp_phase1) return false;
      if (filterScope === 'CONFORMITE_BLOQUEE' && row.etat !== 'conformite_a_verifier') return false;
      if (filterScope === 'EMAIL_MANQUANT' && row.etat !== 'email_a_completer') return false;
      if (filterCountry !== 'ALL' && row.donnees.pays !== filterCountry) return false;
      if (filterPriority !== 'ALL' && row.score.priorite !== filterPriority) return false;
      if (filterStatus !== 'ALL' && row.etat !== filterStatus) return false;
      return true;
    });
  }, [list, filterScope, filterCountry, filterPriority, filterStatus]);

  const uncertainLeads = useMemo(() => {
    const fromState = Array.isArray(state?.envois_incertains) ? state.envois_incertains : [];
    const fromList = list
      .filter((r) => r.etat === 'envoi_incertain' || r.etat === 'envoi_en_cours')
      .map((r) => ({
        _id: r._id,
        etat: r.etat,
        societe: r.donnees?.societe || '',
        email: r.donnees?.email || '',
        pays: r.donnees?.pays || '',
      }));
    const map = new Map();
    for (const item of [...fromState, ...fromList]) {
      if (item && item._id) map.set(item._id, item);
    }
    return Array.from(map.values());
  }, [state?.envois_incertains, list]);

  const canReview = one && ['a_verifier', 'a_valider', 'conformite_a_verifier'].includes(one.etat);
  const oneIsLocal = one && (one.donnees.pays === 'LOCAL_TN' || one.marche === 'LOCAL_TN');
  const oneCatConformite = one?.conformite_pays?.categorie_conformite || (one?.donnees?.pays === 'JP' ? 'OPT_IN_STRICT' : 'B2B_LEGITIMATE_OPT_OUT');

  return (
    <div className="space-y-6" data-testid="prospection-asie">
      <PageTitle
        emoji="🌏"
        titre="Prospection Export Mondiale (HS 1509.20) & Marché Local Tunisien"
        sous="Phase 2 étendue • Recherche douanière hebdomadaire (Volza + Apollo), découverte locale Tunisie (Google Places + Synergie WhatsApp Phase 1), conformité mondiale Fail-Closed, alertes PWA Priorité A et envoi humain à clic unique."
      />

      {error && (
        <p role="alert" className="border border-red-300 bg-red-50 p-3 text-red-800 rounded font-medium">
          {error}
        </p>
      )}
      {info && (
        <p role="status" className="border border-emerald-300 bg-emerald-50 p-3 text-emerald-900 rounded font-medium">
          {info}
        </p>
      )}

      {/* BANDEAU D'ALERTE DISCRET : PROSPECT(S) EN ENVOI INCERTAIN OU EN COURS */}
      {uncertainLeads.length > 0 && (
        <div
          role="alert"
          data-testid="bandeau-envoi-incertain"
          className="border-l-4 border-amber-600 bg-amber-50 p-3.5 rounded shadow-sm text-xs text-amber-950 space-y-2"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="font-bold text-sm flex items-center gap-2">
              <span>⚠️ Vérification SMTP requise ({uncertainLeads.length} envoi(s) au résultat incertain)</span>
            </div>
            <span className="px-2 py-0.5 rounded bg-amber-200 text-amber-950 font-bold">
              Zéro réenvoi automatique (Protection anti-doublon active)
            </span>
          </div>
          <p className="text-amber-900">
            Un envoi est resté en statut <strong>Résultat SMTP incertain</strong> (ou bail d’envoi interrompu). Pour éviter tout double envoi,
            vérifiez dans la boîte « Messages envoyés » de votre fournisseur mail si l’email est parti, puis réconciliez la fiche :
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            {uncertainLeads.map((u) => (
              <div
                key={u._id}
                className="inline-flex items-center gap-2 bg-white border border-amber-300 px-2.5 py-1.5 rounded"
              >
                <span className="font-bold text-slate-900">{u.societe || u._id.slice(0, 8)}</span>
                {u.pays && <span className="text-slate-500">({u.pays})</span>}
                {u.email && <code className="text-amber-900 bg-amber-100/70 px-1.5 py-0.5 rounded">{u.email}</code>}
                <button
                  type="button"
                  onClick={() => open(u._id)}
                  className="bg-amber-700 text-white px-2 py-1 rounded font-bold hover:bg-amber-800"
                >
                  Ouvrir & réconcilier
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* BANDEAU DES FENÊTRES D'OPPORTUNITÉ SAISONNIÈRES */}
      {(state?.fenetre_export_actuelle || state?.fenetre_local_tn_actuelle) && (
        <div className="grid gap-3 sm:grid-cols-2">
          {state?.fenetre_export_actuelle && (
            <div className="border border-emerald-300 bg-emerald-50/70 p-3 rounded text-xs space-y-1">
              <div className="font-bold text-emerald-950 flex items-center justify-between">
                <span>{state.fenetre_export_actuelle.label}</span>
                <span className="px-2 py-0.5 rounded bg-emerald-200 text-emerald-900 font-bold">
                  {state.fenetre_export_actuelle.periode}
                </span>
              </div>
              <p className="text-emerald-900">{state.fenetre_export_actuelle.explication}</p>
            </div>
          )}
          {state?.fenetre_local_tn_actuelle && (
            <div className="border border-sky-300 bg-sky-50/70 p-3 rounded text-xs space-y-1">
              <div className="font-bold text-sky-950 flex items-center justify-between">
                <span>{state.fenetre_local_tn_actuelle.label}</span>
                <span className="px-2 py-0.5 rounded bg-sky-200 text-sky-900 font-bold">
                  {state.fenetre_local_tn_actuelle.periode}
                </span>
              </div>
              <p className="text-sky-900">{state.fenetre_local_tn_actuelle.explication}</p>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-4">
        <StatBlock titre="Prospects qualifiés" items={[['Total en base', total]]} />
        <StatBlock
          titre="Recherche Export (HS 1509.20)"
          items={[
            [
              'Volza + Apollo',
              state?.connecteur_export?.mode_simulation ? 'Prêt (Simulation hors-ligne)' : 'Connecté (API en direct)',
            ],
          ]}
        />
        <StatBlock
          titre="Marché Local (LOCAL_TN)"
          items={[
            [
              'Plancher TND',
              state?.plancher_tnd_local ? `${state.plancher_tnd_local} TND` : '11.200 TND',
            ],
          ]}
        />
        <StatBlock
          titre="Canal Email & PWA"
          items={[
            [
              'Envoi sortant',
              state?.envoi_actif ? 'Autorisé après clic Admin' : 'Verrouillé (Mode test sans envoi)',
            ],
          ]}
        />
      </div>

      {/* SECTION 1 : RECHERCHE AUTOMATIQUE EXPORT & MARCHÉ LOCAL TUNISIEN + AJOUT MANUEL */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* VOLET EXPORT MONDIAL AUTOMATIQUE */}
        <div className="border bg-white p-4 rounded space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-800">Volet 1 • Export Mondial</span>
              <h2 className="font-bold text-base">Recherche douanière automatique (Code HS 1509.20)</h2>
              <p className="text-xs text-slate-600">
                Détection hebdomadaire automatique via <strong>Volza API</strong> (connaissements mondiaux HS 1509.20) couplée à{' '}
                <strong>Apollo.io</strong> pour identifier l’email professionnel de l’acheteur. Tout prospect Priorité A (≥ 70/100) déclenche une notification PWA.
              </p>
            </div>
          </div>
          {state?.derniere_sync_export?.derniere_execution && (
            <p className="text-xs text-slate-500">
              Dernière exécution : <strong>{state.derniere_sync_export.derniere_execution.slice(0, 16).replace('T', ' ')}</strong> par{' '}
              {state.derniere_sync_export.par}
            </p>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              disabled={busy || !state?.actif}
              onClick={triggerExportSync}
              className="bg-emerald-700 text-white px-3 py-2 rounded text-sm font-bold hover:bg-emerald-800 disabled:opacity-40"
            >
              🌍 Lancer la recherche hebdomadaire HS 1509.20 (Volza + Apollo)
            </button>
          </div>
        </div>

        {/* VOLET MARCHÉ LOCAL TUNISIEN (LOCAL_TN) */}
        <div className="border bg-white p-4 rounded space-y-3">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-sky-800">Volet 2 • Marché Intérieur (LOCAL_TN)</span>
            <h2 className="font-bold text-base">Prospection Locale Tunisie & Croisement WhatsApp Phase 1</h2>
            <p className="text-xs text-slate-600">
              Identification via <strong>Google Places API</strong> (épiceries fines, supermarchés premium, hôtels 4★/5★, tables gastronomiques, traiteurs, terroir) et croisement automatique avec vos conversations WhatsApp en souffrance (Étape 0).
            </p>
          </div>
          <div className="flex flex-wrap gap-2 items-center text-xs">
            <span className="font-bold text-slate-700">Villes cibles :</span>
            {DEFAULT_VILLES_TN.map((v) => (
              <label key={v} className="inline-flex items-center gap-1 border px-2 py-1 rounded bg-slate-50 cursor-pointer">
                <input type="checkbox" checked={selectedVillesTn.includes(v)} onChange={() => toggleVilleTn(v)} />
                {v}
              </label>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center justify-between pt-1">
            <select
              aria-label="Segment local tunisien"
              className="border p-2 rounded text-xs bg-white"
              value={selectedSegmentTn}
              onChange={(e) => setSelectedSegmentTn(e.target.value)}
            >
              <option value="ALL">Toutes les cibles premium (Épiceries, Hôtels, Restaurants, Traiteurs…)</option>
              {Object.entries(DEFAULT_SEGMENTS_TN).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
            <button
              type="button"
              disabled={busy || !state?.actif || !selectedVillesTn.length}
              onClick={triggerLocalTnSync}
              className="bg-sky-700 text-white px-3 py-2 rounded text-sm font-bold hover:bg-sky-800 disabled:opacity-40"
            >
              🇹🇳 Scanner le marché local Tunisien
            </button>
          </div>
        </div>
      </div>

      {/* PANNEAU DE GOUVERNANCE DES PAYS "CONFORMITÉ À VÉRIFIER" (FAIL-CLOSED) */}
      {complianceState.pays_bloques_avec_prospects?.length > 0 && (
        <div className="border-2 border-purple-400 bg-purple-50 p-4 rounded space-y-3" data-testid="panneau-conformite-pays">
          <div>
            <h2 className="font-bold text-sm text-purple-950">
              🛡️ Garde-fou de conformité internationale (Fail-Closed) : {complianceState.pays_bloques_avec_prospects.length} pays en attente de validation
            </h2>
            <p className="text-xs text-purple-900">
              Conformément à votre règle de sécurité, le système n’invente jamais une règle juridique qu’il ne connaît pas avec certitude. Les prospects des pays ci-dessous sont bloqués tant que vous n’avez pas validé le régime applicable.
            </p>
          </div>
          <div className="space-y-2">
            {complianceState.pays_bloques_avec_prospects.map((p) => {
              const currentForm = certifyForms[p.code] || {
                categorie_conformite: 'OPT_IN_STRICT',
                loi: `Règle B2B vérifiée pour ${p.nom}`,
                exigence: 'Vérification manuelle par la direction avant tout envoi.',
                langue_defaut: p.langue_defaut || 'en_formel',
              };
              return (
                <div key={p.code} className="border border-purple-300 bg-white p-3 rounded grid gap-2 sm:grid-cols-5 items-center text-xs">
                  <div className="sm:col-span-2">
                    <div className="font-bold text-sm text-slate-900">
                      {p.nom} ({p.code}) · <span className="text-purple-800">{p.nb_prospects_bloques} prospect(s) bloqué(s)</span>
                    </div>
                    <div className="text-slate-600">Sociétés : {p.societes.join(', ')}</div>
                  </div>
                  <select
                    aria-label={`Régime de conformité ${p.code}`}
                    className="border p-2 rounded bg-slate-50"
                    value={currentForm.categorie_conformite}
                    onChange={(e) =>
                      setCertifyForms({
                        ...certifyForms,
                        [p.code]: {...currentForm, categorie_conformite: e.target.value},
                      })
                    }
                  >
                    <option value="OPT_IN_STRICT">Catégorie 1 : Opt-in strict requis (Preuve HTTPS)</option>
                    <option value="B2B_LEGITIMATE_OPT_OUT">Catégorie 2 : Intérêt légitime B2B (Opt-out)</option>
                  </select>
                  <input
                    className="border p-2 rounded"
                    placeholder="Référence / note juridique"
                    value={currentForm.loi}
                    onChange={(e) =>
                      setCertifyForms({
                        ...certifyForms,
                        [p.code]: {...currentForm, loi: e.target.value},
                      })
                    }
                  />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => certifyCountry(p.code)}
                    className="bg-purple-800 text-white px-3 py-2 rounded font-bold hover:bg-purple-900"
                  >
                    Valider la règle {p.code}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* FORMULAIRE D'AJOUT MANUEL (SALON, CARTE DE VISITE, CONTACT LOCAL) & IMPORT CSV SECONDAIRE */}
      <div className="border bg-white p-4 rounded space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-bold text-sm">Contacts rencontrés en personne (Salon, Carte de visite, Recommandation locale)</h2>
            <p className="text-xs text-slate-600">
              Utilisez ce formulaire pour saisir un acheteur rencontré sur un salon ou un établissement tunisien contacté en direct.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="border border-emerald-700 bg-emerald-700 text-white px-3 py-2 text-xs font-bold rounded hover:bg-emerald-800"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? 'Fermer le formulaire manuel' : '➕ Ajouter un prospect manuellement'}
            </button>
          </div>
        </div>

        {showAddForm && (
          <form onSubmit={submitNewLead} className="border-t pt-4 mt-3 grid gap-3 sm:grid-cols-3 bg-slate-50 p-4 rounded">
            <h3 className="sm:col-span-3 font-bold text-sm text-slate-800">
              Nouveau prospect (Export mondial ou Marché Local Tunisien LOCAL_TN)
            </h3>
            <label className="text-xs font-semibold">
              Marché / Pays cible *
              <select
                className="mt-1 block w-full border p-2 rounded text-sm bg-white"
                value={newLead.pays}
                onChange={(e) => {
                  const p = e.target.value;
                  setNewLead({
                    ...newLead,
                    pays: p,
                    source: p === 'LOCAL_TN' ? 'terrain_local' : 'salon',
                    hs: p === 'LOCAL_TN' ? '' : '1509.20',
                  });
                }}
              >
                {countriesOptions.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code === 'LOCAL_TN' ? '🇹🇳 Tunisie — Marché Local (LOCAL_TN)' : `${c.nom} (${c.code}) — ${c.categorie_conformite}`}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold">
              Nom de la société / établissement (sans chiffres ni symboles de prix) *
              <input
                required
                className="mt-1 block w-full border p-2 rounded text-sm bg-white"
                placeholder="Ex: Maison Gourmet ou Hôtel Dar..."
                value={newLead.societe}
                onChange={(e) => setNewLead({...newLead, societe: e.target.value})}
              />
            </label>
            <label className="text-xs font-semibold">
              Email ({newLead.pays === 'LOCAL_TN' ? 'Email local ou Gmail accepté' : 'Email B2B @entreprise uniquement'}) *
              <input
                required={newLead.pays !== 'LOCAL_TN' || !newLead.telephone}
                type="email"
                className="mt-1 block w-full border p-2 rounded text-sm bg-white"
                placeholder={newLead.pays === 'LOCAL_TN' ? 'contact@etablissement.tn' : 'buyer@company.com'}
                value={newLead.email}
                onChange={(e) => setNewLead({...newLead, email: e.target.value})}
              />
            </label>

            {newLead.pays === 'LOCAL_TN' && (
              <>
                <label className="text-xs font-semibold">
                  Ville en Tunisie *
                  <select
                    className="mt-1 block w-full border p-2 rounded text-sm bg-white"
                    value={newLead.ville}
                    onChange={(e) => setNewLead({...newLead, ville: e.target.value})}
                  >
                    {DEFAULT_VILLES_TN.concat(['Autre Tunisie']).map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold">
                  Catégorie d’établissement local *
                  <select
                    className="mt-1 block w-full border p-2 rounded text-sm bg-white"
                    value={newLead.segment_local}
                    onChange={(e) => setNewLead({...newLead, segment_local: e.target.value})}
                  >
                    {Object.entries(DEFAULT_SEGMENTS_TN).map(([k, label]) => (
                      <option key={k} value={k}>{label}</option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold">
                  Téléphone / WhatsApp Tunisien (+216)
                  <input
                    className="mt-1 block w-full border p-2 rounded text-sm bg-white"
                    placeholder="+21674100200"
                    value={newLead.telephone}
                    onChange={(e) => setNewLead({...newLead, telephone: e.target.value})}
                  />
                </label>
                <label className="text-xs font-semibold">
                  Langue d’approche locale
                  <select
                    className="mt-1 block w-full border p-2 rounded text-sm bg-white"
                    value={newLead.langue_approche || 'fr_local'}
                    onChange={(e) => setNewLead({...newLead, langue_approche: e.target.value})}
                  >
                    <option value="fr_local">Français professionnel (Cellule Commerciale)</option>
                    <option value="ar_tn">Français + Arabe tunisien courtois (السلام عليكم وعسلامة)</option>
                  </select>
                </label>
              </>
            )}

            <label className="text-xs font-semibold">
              Nom de l’interlocuteur / acheteur
              <input
                className="mt-1 block w-full border p-2 rounded text-sm bg-white"
                placeholder="Ex: M. Karim Mansour"
                value={newLead.contact}
                onChange={(e) => setNewLead({...newLead, contact: e.target.value})}
              />
            </label>
            <label className="text-xs font-semibold">
              Marque ou gamme repérée (optionnel, sans chiffres)
              <input
                className="mt-1 block w-full border p-2 rounded text-sm bg-white"
                placeholder="Ex: Bella Oliva"
                value={newLead.marque}
                onChange={(e) => setNewLead({...newLead, marque: e.target.value})}
              />
            </label>
            <label className="text-xs font-semibold">
              Source de la preuve *
              <select
                className="mt-1 block w-full border p-2 rounded text-sm bg-white"
                value={newLead.source}
                onChange={(e) => setNewLead({...newLead, source: e.target.value})}
              >
                <option value="salon">Salon / Rencontre en personne (Carte de visite)</option>
                <option value="douane">Douanes / Connaissements (HS 1509.20)</option>
                <option value="google_places">Google Places / Maps Tunisie</option>
                <option value="kompass_tn">Annuaire Kompass Tunisie</option>
                <option value="rne_tn">Registre National des Entreprises (RNE)</option>
                <option value="terrain_local">Prospection terrain directe (Tunisie)</option>
                <option value="registre">Registre officiel des importateurs</option>
                <option value="site">Site web officiel</option>
                <option value="enrichissement">Enrichissement B2B vérifié</option>
              </select>
            </label>
            <label className="text-xs font-semibold">
              Lien HTTPS de la preuve (fiche, site ou registre) *
              <input
                required
                type="url"
                className="mt-1 block w-full border p-2 rounded text-sm bg-white"
                placeholder="https://..."
                value={newLead.url_preuve}
                onChange={(e) => setNewLead({...newLead, url_preuve: e.target.value})}
              />
            </label>
            <label className="text-xs font-semibold">
              Date de la preuve *
              <input
                required
                type="date"
                className="mt-1 block w-full border p-2 rounded text-sm bg-white"
                value={newLead.date_preuve}
                onChange={(e) => setNewLead({...newLead, date_preuve: e.target.value})}
              />
            </label>
            {newLead.pays !== 'LOCAL_TN' && (
              <label className="text-xs font-semibold">
                Code douanier HS (ex: 1509.20)
                <input
                  className="mt-1 block w-full border p-2 rounded text-sm bg-white"
                  placeholder="1509.20"
                  value={newLead.hs}
                  onChange={(e) => setNewLead({...newLead, hs: e.target.value})}
                />
              </label>
            )}
            <label className="text-xs font-semibold">
              Positionnement / Canal
              <select
                className="mt-1 block w-full border p-2 rounded text-sm bg-white"
                value={newLead.canal}
                onChange={(e) => setNewLead({...newLead, canal: e.target.value})}
              >
                <option value="premium">Premium / Épicerie fine / Horeca 4★-5★ (+25 pts)</option>
                <option value="generaliste">Généraliste / Grande distribution (+12 pts)</option>
                <option value="">Non renseigné (0 pt)</option>
              </select>
            </label>
            <label className="text-xs font-semibold">
              Volume cible estimé
              <select
                className="mt-1 block w-full border p-2 rounded text-sm bg-white"
                value={newLead.volume}
                onChange={(e) => setNewLead({...newLead, volume: e.target.value})}
              >
                <option value="palette_ou_plus">Palette, conteneur ou réassort régulier (+20 pts)</option>
                <option value="petite_commande">Petite commande / Test (+12 pts)</option>
                <option value="">Non renseigné (0 pt)</option>
              </select>
            </label>
            <label className="text-xs font-semibold flex items-center gap-2 pt-4">
              <input
                type="checkbox"
                checked={newLead.autorisation_email}
                onChange={(e) => setNewLead({...newLead, autorisation_email: e.target.checked})}
              />
              Consentement / Opt-in documenté (requis pour JP, CA, AU, DE, AT, IT, PL)
            </label>
            {newLead.autorisation_email && (
              <label className="text-xs font-semibold sm:col-span-2">
                URL HTTPS de preuve d’opt-in / publication B2B
                <input
                  type="url"
                  className="mt-1 block w-full border p-2 rounded text-sm bg-white"
                  placeholder="https://..."
                  value={newLead.url_autorisation}
                  onChange={(e) => setNewLead({...newLead, url_autorisation: e.target.value})}
                />
              </label>
            )}
            <div className="sm:col-span-3 flex justify-end gap-2 pt-2">
              <button type="submit" disabled={busy || !state?.actif} className="bg-emerald-700 text-white px-4 py-2 rounded text-sm font-bold disabled:opacity-40">
                Créer la fiche et préparer le message
              </button>
            </div>
          </form>
        )}

        <details className="text-xs text-slate-500 pt-1">
          <summary className="cursor-pointer font-semibold">Import CSV secondaire (secours / annuaire salon)</summary>
          <div className="flex flex-wrap gap-3 items-center pt-2">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL ?? ''}/api/prospection-asie/modele-csv`}
              download="modele_prospection_asie.csv"
              className={b}
            >
              📥 Modèle CSV
            </a>
            <input
              aria-label="Fichier CSV"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setCsv(e.target.files[0] || null)}
              className="text-sm"
            />
            <button className={b} disabled={busy || !state?.actif || !csv} onClick={() => action(importCsv)}>
              Importer le fichier CSV
            </button>
          </div>
        </details>
      </div>

      {/* BARRE DE FILTRES PAR VOLET, PAYS, PRIORITÉ ET STATUT */}
      <div className="flex flex-wrap gap-3 items-center bg-white border p-3 rounded text-sm">
        <span className="font-bold text-slate-700">Volet :</span>
        <select aria-label="Filtrer par volet" className="border p-1.5 rounded font-semibold" value={filterScope} onChange={(e) => setFilterScope(e.target.value)}>
          <option value="ALL">Tous les marchés ({list.length})</option>
          <option value="EXPORT">🌍 Export Mondial uniquement (HS 1509.20)</option>
          <option value="LOCAL_TN">🇹🇳 Marché Local Tunisien (LOCAL_TN)</option>
          <option value="WA_PHASE1">💬 Déjà connus dans WhatsApp Phase 1 (Étape 0)</option>
          <option value="CONFORMITE_BLOQUEE">🛡️ Pays à vérifier (Conformité bloquée)</option>
          <option value="EMAIL_MANQUANT">✉️ Email acheteur à compléter (Volza)</option>
        </select>
        <select aria-label="Filtrer par pays" className="border p-1.5 rounded" value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)}>
          <option value="ALL">Tous les pays / marchés</option>
          {countriesOptions.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code === 'LOCAL_TN' ? '🇹🇳 LOCAL_TN (Tunisie)' : `${c.nom} (${c.code})`}
            </option>
          ))}
        </select>
        <select aria-label="Filtrer par priorité" className="border p-1.5 rounded" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
          <option value="ALL">Toutes les priorités</option>
          <option value="A">🔔 Priorité A (≥ 70/100)</option>
          <option value="B">Priorité B (45 à 69/100)</option>
          <option value="C">Priorité C (&lt; 45 — bloqué)</option>
        </select>
        <select aria-label="Filtrer par statut" className="border p-1.5 rounded" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="ALL">Tous les statuts</option>
          {Object.entries(labels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {/* TABLEAU PRINCIPAL DES PROSPECTS */}
      <section className="overflow-x-auto border bg-white rounded">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['Entreprise & Marché', 'Conformité & Fenêtre Saisonnière', 'Score / Priorité', 'Statut', 'Action'].map((k) => (
                <th className="p-3" key={k}>{k}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredList.map((row) => {
              const isLocal = row.donnees.pays === 'LOCAL_TN' || row.marche === 'LOCAL_TN';
              const catConf = row.conformite_pays?.categorie_conformite || (isLocal ? 'LOCAL_TN_B2B' : row.donnees.pays === 'JP' ? 'OPT_IN_STRICT' : 'B2B_LEGITIMATE_OPT_OUT');
              const confBadge = CONFORMITE_BADGES[catConf] || CONFORMITE_BADGES.B2B_LEGITIMATE_OPT_OUT;
              return (
                <tr className="border-t hover:bg-slate-50" key={row._id}>
                  <td className="p-3 font-medium space-y-0.5">
                    <div>
                      {row.donnees.societe} ·{' '}
                      <span className="font-bold">
                        {isLocal ? `🇹🇳 LOCAL_TN (${row.donnees.ville || 'Tunisie'})` : row.donnees.pays}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500">
                      {row.donnees.email || <span className="text-orange-700 font-semibold">Email B2B à compléter</span>}
                      {row.donnees.telephone ? ` · 📞 ${row.donnees.telephone}` : ''}
                    </div>
                    {row.synergie_wa?.existant_whatsapp_phase1 && (
                      <div className="inline-block bg-indigo-50 text-indigo-900 border border-indigo-300 px-2 py-0.5 rounded text-xs font-bold">
                        💬 Déjà dans WhatsApp Phase 1 (Étape 0)
                      </div>
                    )}
                  </td>
                  <td className="p-3 space-y-1 text-xs">
                    <div>
                      <span className={`inline-block border px-2 py-0.5 rounded font-semibold ${confBadge.cls}`}>
                        {confBadge.label}
                      </span>
                    </div>
                    {row.fenetre_saisonniere?.active && (
                      <div className="text-emerald-800 font-semibold">
                        {row.fenetre_saisonniere.label}
                      </div>
                    )}
                    <div className="text-slate-500">Source : {row.donnees.source}</div>
                  </td>
                  <td className="p-3 font-bold">
                    <div>
                      {row.score.total}/100 · Priorité {row.score.priorite}
                    </div>
                    {row.score.total >= 70 && (
                      <span className="text-xs text-emerald-800 font-semibold">🔔 Alerte PWA Priorité A</span>
                    )}
                  </td>
                  <td className="p-3">
                    <span className={`inline-block border px-2 py-0.5 rounded text-xs font-bold ${badgeColors[row.etat] || ''}`}>
                      {labels[row.etat] || row.etat}
                    </span>
                  </td>
                  <td className="p-3">
                    <button className={b} disabled={busy} onClick={() => action(() => open(row._id))}>
                      Examiner
                    </button>
                  </td>
                </tr>
              );
            })}
            {!filteredList.length && (
              <tr>
                <td colSpan="5" className="p-4 text-slate-500">Aucun prospect correspondant.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <div className="flex gap-2 items-center">
        <button className={b} disabled={page === 1} onClick={() => setPage(page - 1)}>Précédent</button>
        <span className="text-sm">Page {page}</span>
        <button className={b} disabled={page * 50 >= total} onClick={() => setPage(page + 1)}>Suivant</button>
      </div>

      {/* FICHE DÉTAILLÉE DU PROSPECT */}
      {one && (
        <section className="border-2 border-slate-300 bg-white p-5 rounded space-y-4" aria-label="Fiche prospect">
          <div className="flex justify-between items-start gap-2">
            <div>
              <h2 className="font-bold text-lg">
                {one.donnees.societe} ({oneIsLocal ? `LOCAL_TN · ${one.donnees.ville || 'Tunisie'}` : one.donnees.pays}) ·{' '}
                <span className="text-emerald-800">{labels[one.etat] || one.etat}</span>
              </h2>
              <p className="text-sm text-slate-600">
                Contact : <strong>{one.donnees.contact || 'Non identifié'}</strong> · Email :{' '}
                <strong>{one.donnees.email || 'À compléter'}</strong>
                {one.donnees.telephone ? ` · Téléphone/WhatsApp : ${one.donnees.telephone}` : ''}
                {one.donnees.marque ? ` · Référence repérée : ${one.donnees.marque}` : ''}
              </p>
            </div>
            <button className={b} onClick={() => setOne(null)}>Fermer</button>
          </div>

          {/* ALERTE SYNERGIE WHATSAPP ÉTAPE 0 / PHASE 1 */}
          {one.synergie_wa?.existant_whatsapp_phase1 && (
            <div className="border-2 border-indigo-400 bg-indigo-50 p-3 rounded text-sm space-y-1">
              <div className="font-bold text-indigo-950">
                💬 Synergie Étape 0 / Phase 1 détectée : Ce contact existe déjà dans vos conversations WhatsApp !
              </div>
              <p className="text-xs text-indigo-900">{one.synergie_wa.recommandation}</p>
              <div className="pt-1">
                <Link
                  to="/commercial-whatsapp"
                  className="inline-block bg-indigo-800 text-white px-3 py-1.5 rounded text-xs font-bold hover:bg-indigo-900"
                >
                  Ouvrir la reprise WhatsApp Phase 1 pour ce contact →
                </Link>
              </div>
            </div>
          )}

          {/* BANDEAU FENÊTRE D'OPPORTUNITÉ SAISONNIÈRE DE LA FICHE */}
          {one.fenetre_saisonniere && (
            <div className="border border-emerald-300 bg-emerald-50/60 p-3 rounded text-xs">
              <strong>Fenêtre d’opportunité saisonnière ({one.fenetre_saisonniere.periode}) :</strong>{' '}
              {one.fenetre_saisonniere.label} — {one.fenetre_saisonniere.explication}
            </div>
          )}

          {/* DÉCOMPOSITION DU SCORE /100 */}
          <div className="grid gap-2 sm:grid-cols-5 bg-slate-50 p-3 rounded border text-xs">
            <div><strong>Preuve source :</strong> {one.score.details.import}/25 pts</div>
            <div><strong>Canal :</strong> {one.score.details.canal}/25 pts</div>
            <div><strong>Volume :</strong> {one.score.details.volume}/20 pts</div>
            <div><strong>Solvabilité :</strong> {one.score.details.solvabilite}/15 pts</div>
            <div><strong>Intérêt / Saison :</strong> {one.score.details.interet}/15 pts</div>
          </div>

          <p className="text-sm">
            Source : <strong>{one.donnees.source}</strong> · Date preuve : <strong>{one.donnees.date_preuve}</strong> · Référence :{' '}
            <strong>{one.donnees.reference_preuve || 'Non fournie'}</strong>
            {!oneIsLocal && (
              <>
                {' '}· Code HS : <strong>{one.donnees.hs || 'Non renseigné'}</strong>
              </>
            )}{' '}
            —{' '}
            <a className="text-blue-700 underline break-all font-semibold" href={one.donnees.url_preuve} target="_blank" rel="noopener noreferrer">
              Ouvrir la preuve source ↗
            </a>
          </p>

          {/* ENCADRÉ DE CONFORMITÉ PAR PAYS */}
          {one.conformite_pays && (
            <div className="border border-amber-300 bg-amber-50 p-3 rounded text-xs space-y-1">
              <div className="font-bold text-amber-950">
                Règle de conformité ({one.conformite_pays.nom} · {one.conformite_pays.categorie_conformite}) — {one.conformite_pays.loi}
              </div>
              <p className="text-amber-900">{one.conformite_pays.exigence}</p>
              <p className="text-slate-700">
                Langue & formalité automatiques : <strong>{one.conformite_pays.formalite}</strong>
              </p>
              {oneCatConformite === 'OPT_IN_STRICT' && (
                <p className="font-semibold text-amber-950">
                  Preuve d’opt-in / publication B2B autorisée : <strong>{one.donnees.autorisation_email ? 'Oui' : 'Non'}</strong>{' '}
                  {one.donnees.url_autorisation && (
                    <a href={one.donnees.url_autorisation} target="_blank" rel="noopener noreferrer" className="underline">
                      Ouvrir la preuve d’autorisation ↗
                    </a>
                  )}
                </p>
              )}
            </div>
          )}

          {/* COMPLÉTION D'EMAIL SI CONNAISSEMENT VOLZA SANS EMAIL */}
          {one.etat === 'email_a_completer' && (
            <div className="border-2 border-orange-300 bg-orange-50 p-4 rounded space-y-2">
              <h3 className="font-bold text-sm text-orange-950">
                ✉️ Connaissement douanier détecté sans email acheteur : compléter l’email B2B
              </h3>
              <p className="text-xs text-orange-900">
                Volza a détecté cette société sous le code HS 1509.20, mais aucun email nominatif n’était présent. Renseignez l’email professionnel de l’acheteur pour débloquer la préparation du message.
              </p>
              <div className="grid gap-2 sm:grid-cols-3">
                <input
                  type="email"
                  className="border p-2 rounded text-sm bg-white"
                  placeholder="buyer@company.com"
                  value={completeEmailValue}
                  onChange={(e) => setCompleteEmailValue(e.target.value)}
                />
                <input
                  className="border p-2 rounded text-sm bg-white"
                  placeholder="Nom de l'acheteur (optionnel)"
                  value={completeContactValue}
                  onChange={(e) => setCompleteContactValue(e.target.value)}
                />
                <button
                  type="button"
                  disabled={busy || !completeEmailValue}
                  className="bg-orange-700 text-white px-3 py-2 rounded text-sm font-bold"
                  onClick={() =>
                    action(async () => {
                      const saved = await asieApi(`/prospects/${one._id}/completer-email`, 'POST', {
                        version: one.version,
                        email: completeEmailValue,
                        contact: completeContactValue,
                        note: completeNoteValue || 'Email B2B complété manuellement',
                      });
                      await open(saved._id);
                      setInfo('Email professionnel enregistré et brouillon généré.');
                    })
                  }
                >
                  Enregistrer l’email B2B
                </button>
              </div>
            </div>
          )}

          {/* ÉTAPE A — VÉRIFICATION HUMAINE */}
          {canReview && (
            <div className="border p-3 rounded bg-slate-50 space-y-2">
              <h3 className="font-bold text-sm">Étape A — Vérification humaine de la preuve, de la conformité et du destinataire</h3>
              <label className="block text-sm">
                <input
                  type="checkbox"
                  checked={one.preuve_verifiee}
                  onChange={(e) => setOne({...one, preuve_verifiee: e.target.checked})}
                />{' '}
                J’ai lu et vérifié la source et les signaux du score ({one.score.total}/100)
              </label>
              <label className="block text-sm">
                <input
                  type="checkbox"
                  checked={one.contact_verifie}
                  onChange={(e) => setOne({...one, contact_verifie: e.target.checked})}
                />{' '}
                J’ai vérifié que les coordonnées appartiennent bien à cet établissement professionnel B2B
              </label>
              {oneCatConformite === 'OPT_IN_STRICT' && (
                <div className="border border-amber-300 bg-white p-2 rounded space-y-2">
                  <label className="block text-xs font-bold">
                    <input
                      type="checkbox"
                      checked={Boolean(one.donnees.autorisation_email)}
                      onChange={(e) =>
                        setOne({
                          ...one,
                          donnees: {...one.donnees, autorisation_email: e.target.checked},
                        })
                      }
                    />{' '}
                    Preuve d’opt-in / publication B2B vérifiée pour {one.conformite_pays?.nom || one.donnees.pays} (obligatoire)
                  </label>
                  <input
                    type="url"
                    className="block border p-2 w-full rounded text-xs"
                    placeholder="URL HTTPS de la preuve d'autorisation ou d'annuaire B2B (https://...)"
                    value={one.donnees.url_autorisation || ''}
                    onChange={(e) =>
                      setOne({
                        ...one,
                        donnees: {...one.donnees, url_autorisation: e.target.value},
                      })
                    }
                  />
                </div>
              )}
              <label className="block text-sm">
                Note de vérification
                <input
                  className="block border p-2 w-full rounded bg-white mt-1"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Ex: Importateur ou établissement vérifié"
                />
              </label>
              <button
                className={b}
                disabled={busy}
                onClick={() =>
                  action(async () => {
                    const saved = await asieApi(`/prospects/${one._id}/verifier`, 'POST', {
                      version: one.version,
                      preuve_verifiee: one.preuve_verifiee,
                      contact_verifie: one.contact_verifie,
                      autorisation_email: one.donnees.autorisation_email,
                      url_autorisation: one.donnees.url_autorisation,
                      note,
                    });
                    await open(saved._id);
                    setInfo('Vérification enregistrée.');
                  })
                }
              >
                Enregistrer la vérification
              </button>
            </div>
          )}

          {/* ÉTAPE B — BROUILLON SOUMIS À VALIDATION */}
          <div className="border p-4 rounded space-y-3">
            <div className="flex flex-wrap justify-between items-center gap-2">
              <h3 className="font-bold text-sm">
                Étape B — Brouillon d’approche « Cellule Commerciale » {one.mail.personnalise ? '(Personnalisé)' : '(Modèle standard)'}
              </h3>
              {canReview && (
                <div className="flex flex-wrap gap-2 items-center">
                  {oneIsLocal && (
                    <select
                      aria-label="Choisir la langue locale"
                      className="border p-2 rounded text-xs bg-white font-semibold"
                      value={editLangue || 'fr_local'}
                      onChange={(e) => {
                        const lg = e.target.value;
                        setEditLangue(lg);
                        action(async () => {
                          const saved = await asieApi(`/prospects/${one._id}/brouillon`, 'POST', {
                            version: one.version,
                            objet: editObjet || 'reset',
                            texte: editTexte || 'reset text placeholder minimum length',
                            traduction_fr: editTraduction || 'reset traduction',
                            reinitialiser: true,
                            langue_approche: lg,
                          });
                          await open(saved._id);
                          setInfo(`Modèle local régénéré (${lg === 'ar_tn' ? 'Français + Arabe tunisien' : 'Français professionnel'}).`);
                        });
                      }}
                    >
                      <option value="fr_local">🇹🇳 Français professionnel (Cellule Commerciale)</option>
                      <option value="ar_tn">🇹🇳 Français + Arabe tunisien (السلام عليكم وعسلامة)</option>
                    </select>
                  )}
                  <button
                    type="button"
                    className={b}
                    disabled={busy}
                    onClick={() =>
                      action(async () => {
                        const saved = await asieApi(`/prospects/${one._id}/brouillon`, 'POST', {
                          version: one.version,
                          objet: editObjet,
                          texte: editTexte,
                          traduction_fr: editTraduction,
                          reinitialiser: false,
                          langue_approche: editLangue || undefined,
                        });
                        await open(saved._id);
                        setInfo('Brouillon enregistré et contrôlé par le garde-fou.');
                      })
                    }
                  >
                    💾 Enregistrer les modifications
                  </button>
                  {one.mail.personnalise && (
                    <button
                      type="button"
                      className={b}
                      disabled={busy}
                      onClick={() =>
                        action(async () => {
                          const saved = await asieApi(`/prospects/${one._id}/brouillon`, 'POST', {
                            version: one.version,
                            objet: editObjet || 'reset',
                            texte: editTexte || 'reset text placeholder minimum length',
                            traduction_fr: editTraduction || 'reset traduction',
                            reinitialiser: true,
                            langue_approche: editLangue || undefined,
                          });
                          await open(saved._id);
                          setInfo('Modèle standard restauré.');
                        })
                      }
                    >
                      ↺ Réinitialiser le modèle
                    </button>
                  )}
                </div>
              )}
            </div>
            <p className="text-xs text-slate-600">
              {oneIsLocal
                ? `Garde-fou Marché Local (LOCAL_TN) : pas d’Incoterm ni de douane, mais tout prix en TND inférieur au seuil plancher (${one.plancher_tnd_actif || state?.plancher_tnd_local || '11.200'} TND) ou toute remise est bloqué automatiquement.`
                : 'Garde-fou Export : bloque automatiquement tout prix, devise (EUR, USD, TND), Incoterm (FOB, CIF, EXW) ou promesse de remise.'}
            </p>
            <label className="block text-xs font-bold">
              Objet de l’email
              <input
                aria-label="Objet de l'email"
                disabled={!canReview || busy}
                className="mt-1 block w-full border p-2 rounded text-sm font-normal bg-slate-50"
                value={editObjet}
                onChange={(e) => setEditObjet(e.target.value)}
              />
            </label>
            <label className="block text-xs font-bold">
              Corps du message (envoyé au prospect)
              <textarea
                aria-label="Corps de l'email"
                rows={7}
                disabled={!canReview || busy}
                className="mt-1 block w-full border p-3 rounded text-sm font-sans bg-slate-50"
                value={editTexte}
                onChange={(e) => setEditTexte(e.target.value)}
              />
            </label>
            <details open className="border rounded p-2 bg-amber-50/40">
              <summary className="font-bold text-xs cursor-pointer">Traduction française de contrôle (pour vous)</summary>
              <textarea
                aria-label="Traduction française"
                rows={3}
                disabled={!canReview || busy}
                className="mt-2 block w-full border p-2 rounded text-sm bg-white"
                value={editTraduction}
                onChange={(e) => setEditTraduction(e.target.value)}
              />
            </details>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              className="bg-emerald-700 text-white px-4 py-3 rounded font-bold disabled:opacity-40"
              disabled={busy || one.etat !== 'a_valider' || !state?.envoi_actif || !state?.smtp_configure}
              onClick={() =>
                action(async () => {
                  await asieApi(`/prospects/${one._id}/approuver`, 'POST', {version: one.version, empreinte: one.empreinte});
                  await open(one._id);
                  setInfo('Email envoyé au serveur SMTP.');
                })
              }
            >
              ✉️ Approuver et envoyer cet email
            </button>

            {canReview && (
              <div className="flex items-center gap-2">
                <input
                  className="border p-2 rounded text-sm"
                  aria-label="Motif du refus"
                  value={refusalReason}
                  onChange={(e) => setRefusalReason(e.target.value)}
                  placeholder="Motif du refus"
                />
                <button
                  className={b}
                  disabled={busy || refusalReason.length < 3}
                  onClick={() =>
                    action(async () => {
                      await asieApi(`/prospects/${one._id}/refuser`, 'POST', {version: one.version, raison: refusalReason});
                      await open(one._id);
                    })
                  }
                >
                  Refuser ce dossier
                </button>
              </div>
            )}
          </div>

          {['envoi_incertain', 'envoi_en_cours'].includes(one.etat) && (
            <div className="border border-amber-400 bg-amber-50 p-4 rounded space-y-2">
              <p className="font-bold text-sm text-amber-900">
                ⚠️ Résultat SMTP incertain : vérifiez d’abord la boîte « Messages envoyés » de votre messagerie avant de trancher.
              </p>
              <input
                className="border p-2 rounded text-sm w-full bg-white"
                placeholder="Note de vérification (ex: Vérifié dans la boîte Envoyés à 14h10)"
                value={reconcileNote}
                onChange={(e) => setReconcileNote(e.target.value)}
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className={b}
                  disabled={busy || reconcileNote.length < 3}
                  onClick={() =>
                    action(async () => {
                      await asieApi(`/prospects/${one._id}/reconcilier-envoi`, 'POST', {
                        version: one.version,
                        envoye_confirme: true,
                        note: reconcileNote,
                      });
                      await open(one._id);
                    })
                  }
                >
                  ✅ L’email est bien parti (Marquer Envoyé)
                </button>
                <button
                  type="button"
                  className={b}
                  disabled={busy || reconcileNote.length < 3}
                  onClick={() =>
                    action(async () => {
                      await asieApi(`/prospects/${one._id}/reconcilier-envoi`, 'POST', {
                        version: one.version,
                        envoye_confirme: false,
                        note: reconcileNote,
                      });
                      await open(one._id);
                    })
                  }
                >
                  🔄 L’email n’est pas parti (Réautoriser l’envoi)
                </button>
              </div>
            </div>
          )}

          {/* LISTE NOIRE PERMANENTE UNIFIÉE (EXPORT & LOCAL_TN) */}
          <div className="border-t pt-3 space-y-2">
            <h3 className="font-bold text-sm">
              Opposition / Liste noire permanente (commune Export & Marché Local Tunisien)
            </h3>
            <div className="flex flex-wrap gap-2">
              <input
                className="border p-2 rounded text-sm"
                aria-label="Email à bloquer"
                placeholder="Email à bloquer"
                value={suppressEmail}
                onChange={(e) => setSuppressEmail(e.target.value)}
              />
              <input
                className="border p-2 rounded text-sm"
                aria-label="Domaine à bloquer"
                placeholder="Domaine (ex: company.com)"
                value={suppressDomain}
                onChange={(e) => setSuppressDomain(e.target.value)}
              />
              <input
                className="border p-2 rounded text-sm"
                aria-label="Téléphone à bloquer"
                placeholder="Téléphone (+216...)"
                value={suppressPhone}
                onChange={(e) => setSuppressPhone(e.target.value)}
              />
              <input
                className="border p-2 rounded text-sm"
                aria-label="Raison"
                value={suppressReason}
                onChange={(e) => setSuppressReason(e.target.value)}
                placeholder="Raison de l'opposition"
              />
              <button
                className={b}
                disabled={busy || suppressReason.length < 3 || (!suppressEmail && !suppressDomain && !suppressPhone)}
                onClick={() =>
                  action(async () => {
                    await asieApi('/oppositions', 'POST', {
                      email: suppressEmail,
                      domaine: suppressDomain,
                      telephone: suppressPhone,
                      raison: suppressReason,
                    });
                    await open(one._id);
                  })
                }
              >
                Bloquer définitivement
              </button>
            </div>
          </div>

          <details className="text-xs text-slate-600">
            <summary className="cursor-pointer font-bold">Historique d’audit de la fiche ({one.audit?.length || 0})</summary>
            <ul className="mt-2 space-y-1 border bg-slate-50 p-3 rounded">
              {(one.audit || []).map((a, idx) => (
                <li key={idx}>
                  <strong>{a.le}</strong> — <span className="font-mono">{a.action}</span> {a.par ? `par ${a.par}` : ''}{' '}
                  {a.note || a.raison || a.reference || ''}
                </li>
              ))}
            </ul>
          </details>
        </section>
      )}
    </div>
  );
}
